import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        submit:
          "bg-primary text-primary-foreground font-semibold hover:bg-primary/90",
        cancel:
          "bg-slate-500 text-white font-semibold hover:bg-slate-600",
        add:
          "rounded-control bg-primary text-primary-foreground font-extrabold hover:bg-primary/90 max-md:w-full",
        success:
          "bg-success text-success-foreground font-bold hover:bg-success/90",
        logout:
          "w-full rounded-card border-0 bg-logout-gradient px-3.5 py-3 text-base font-black text-white shadow-btn-logout transition-[transform,box-shadow] duration-200 hover:-translate-y-px hover:shadow-btn-logout-hover",
        icon:
          "rounded-xl border-0 bg-slate-100 text-slate-700 hover:bg-slate-200",
        mobileMenu:
          "fixed top-3 right-3 z-mobile-menu hidden h-11 w-11 items-center justify-center rounded-card border border-border bg-white text-xl font-black text-slate-900 shadow-mobile-menu max-lg:inline-flex max-[480px]:h-10 max-[480px]:w-10 max-[480px]:rounded-xl",
        loginSubmit:
          "mt-2.5 w-full rounded-card border-0 bg-[linear-gradient(135deg,#4f46e5,#0e7490)] text-base font-black text-white shadow-[0_14px_28px_rgba(14,116,144,0.22)] transition-[transform,box-shadow,opacity] duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_32px_rgba(14,116,144,0.28)] disabled:cursor-not-allowed disabled:opacity-[0.68] disabled:hover:translate-y-0",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-9 w-9",
        close: "h-9 w-9 rounded-xl text-xl font-black",
        login: "h-12 px-3.5 py-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
